import React, { useState, useRef, useEffect } from "react";
import "./MerchandisePage.css";
import { db, storage } from "./firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import QRCode from "qrcode";

// TEMP: Firebase Storage quota exceeded. Set to true to restore direct slip uploads.
const FILE_UPLOADS_ENABLED = false;

const TSHIRT_PRICE = 2500;
const SIZES = ["2XS", "XS", "S", "M", "L", "XL", "2XL", "3XL"];

export default function MerchandisePage() {
  const [showSizeChart, setShowSizeChart] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  });
  const [size, setSize] = useState("M");
  const [quantity, setQuantity] = useState(1);
  const [slipFile, setSlipFile] = useState(null);
  const [slipPreview, setSlipPreview] = useState(null);
  const [slipLink, setSlipLink] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [copied, setCopied] = useState(false);

  // Success state
  const [orderResult, setOrderResult] = useState(null);

  const fileInputRef = useRef(null);

  // Scroll animations observer (mirroring About.jsx)
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -40px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !entry.target.classList.contains("revealed")) {
          entry.target.classList.add("revealed");
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const animatable = document.querySelectorAll(
      ".merch-section, .merch-card, .merch-stat-item, .merch-file-info span"
    );
    animatable.forEach((el) => {
      el.classList.add("animate-on-scroll");
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [orderResult]);

  const formatLKR = (value) =>
    new Intl.NumberFormat("en-LK", {
      style: "currency",
      currency: "LKR",
      maximumFractionDigits: 0,
    }).format(value);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errorMessage) setErrorMessage("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSlipFile(file);
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => setSlipPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setSlipPreview(null);
      }
      if (errorMessage) setErrorMessage("");
    }
  };

  const handleCopyAccount = () => {
    navigator.clipboard.writeText("032200360030474");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName.trim()) {
      setErrorMessage("Please enter your First Name.");
      return;
    }
    if (!formData.lastName.trim()) {
      setErrorMessage("Please enter your Last Name.");
      return;
    }
    if (!formData.phone.trim()) {
      setErrorMessage("Please enter your Phone / Contact Number.");
      return;
    }
    if (!formData.email.trim()) {
      setErrorMessage("Please enter your Email Address.");
      return;
    }
    if (FILE_UPLOADS_ENABLED && !slipFile) {
      setErrorMessage("Please upload your bank payment slip to complete your order.");
      return;
    }
    if (!FILE_UPLOADS_ENABLED && !/^https?:\/\/\S+$/i.test(slipLink.trim())) {
      setErrorMessage("Please paste a valid Google Drive link (https://...) to your bank payment slip.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      let slipUrl;
      let slipName;

      if (FILE_UPLOADS_ENABLED) {
        // 1. Upload payment slip to Firebase Storage
        const timestamp = Date.now();
        const safeName = slipFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const filePath = `orders/${timestamp}_${safeName}`;
        const stRef = storageRef(storage, filePath);
        await uploadBytes(stRef, slipFile);
        slipUrl = await getDownloadURL(stRef);
        slipName = slipFile.name;
      } else {
        // TEMP: Firebase Storage quota exceeded - use Google Drive link
        slipUrl = slipLink.trim();
        slipName = "Google Drive Link";
      }

      const totalAmount = TSHIRT_PRICE * quantity;
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;

      // 2. Save order to Firestore
      const orderPayload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        fullName,
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        admissionNumber: "N/A",
        size,
        quantity,
        item: "BTUI Signature Tee Pack",
        cart: [
          {
            id: 1,
            name: "BTUI Signature Tee Pack",
            quantity,
            price: TSHIRT_PRICE,
            size,
          },
        ],
        total: totalAmount,
        slipUrl,
        slipFileName: slipName,
        status: "pending",
        timestamp: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, "orders"), orderPayload);
      const newOrderId = docRef.id;

      // 3. Generate QR code for verification
      const qrDataUrl = await QRCode.toDataURL(newOrderId, {
        width: 250,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
      });

      setOrderResult({
        orderId: newOrderId,
        qrCode: qrDataUrl,
        fullName,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        size,
        quantity,
        total: totalAmount,
        slipFileName: slipName,
        slipUrl,
      });
    } catch (err) {
      console.error("Order submission failed:", err);
      setErrorMessage("An error occurred while processing your order. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadQR = () => {
    if (!orderResult?.qrCode) return;
    const link = document.createElement("a");
    link.href = orderResult.qrCode;
    link.download = `BTUI-Order-${orderResult.orderId}.png`;
    link.click();
  };

  const handleReset = () => {
    setOrderResult(null);
    setFormData({ firstName: "", lastName: "", phone: "", email: "" });
    setSize("M");
    setQuantity(1);
    setSlipFile(null);
    setSlipPreview(null);
    setSlipLink("");
    setErrorMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="merchandise-container">
      <div className="merchandise-container-outer">
        <div className="merchandise-container-inner">
          {/* Header Label (matching About.jsx char animations) */}
          <div className="merchandise-header-label">
            <span className="char">B</span>
            <span className="char">T</span>
            <span className="char">U</span>
            <span className="char">I</span>
            <span className="char">'</span>
            <span className="char">2</span>
            <span className="char">6</span>
            <span className="char separator">/</span>
            <span className="char">M</span>
            <span className="char">E</span>
            <span className="char">R</span>
            <span className="char">C</span>
            <span className="char">H</span>
            <span className="char">A</span>
            <span className="char">N</span>
            <span className="char">D</span>
            <span className="char">I</span>
            <span className="char">S</span>
            <span className="char">E</span>
          </div>

          {/* Hero Section (matching About.jsx hero typography) */}
          <div className="merchandise-hero">
            <h1 className="merchandise-hero-title">
              <span className="word">BTUI</span>
              <span className="word">SIGNATURE</span>
              <span className="word">TEE</span>
              <span className="word">PACK</span>
            </h1>

            <div className="merchandise-hero-description">
              <p className="desc-line">
                The official commemorative Committee Edition T-Shirt of Beyond the
                User Interface 2026, organized by the Royal College Computer Society.
              </p>
            </div>
          </div>

          {/* Front & Back T-Shirt Showcase Display (only front and back view) */}
          <div className="merch-section showcase-section">
            <div className="section-label">01 // PRODUCT DESIGN & PREVIEW</div>
            <div className="tshirt-visual-card">
              <div className="tshirt-image-wrap">
                <img
                  src="/tshirt-banner.jpg"
                  alt="BTUI Signature T-Shirt Front and Back View"
                  className="tshirt-preview-image"
                />
                <div className="tshirt-image-badge">
                  <span>OFFICIAL DESIGN // FRONT & BACK</span>
                </div>
                <div className="tshirt-price-badge">
                  <span>{formatLKR(TSHIRT_PRICE)}</span>
                </div>
              </div>

              {/* Size guide trigger */}
              <div className="size-guide-strip">
                <button
                  type="button"
                  className="size-guide-trigger-btn"
                  onClick={() => setShowSizeChart(true)}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.3 8.7 8.7 21.3c-.4.4-1 .4-1.4 0L2.7 16.7c-.4-.4-.4-1 0-1.4L15.3 2.7c.4-.4 1-.4 1.4 0l4.6 4.6c.4.4.4 1 0 1.4z" />
                    <path d="m14.5 3.5 1 1" />
                    <path d="m11.5 6.5 2 2" />
                    <path d="m8.5 9.5 1 1" />
                    <path d="m5.5 12.5 2 2" />
                  </svg>
                  <span>VIEW T-SHIRT MEASUREMENTS & SIZE CHART</span>
                </button>
              </div>
            </div>
          </div>

          {/* Order & Payment Form Section */}
          <div className="merch-section order-section">
            <div className="section-label">02 // ORDER & PAYMENT</div>

            {!orderResult ? (
              <div className="order-form-container">
                <form onSubmit={handleSubmit} className="merch-order-form">
                  {errorMessage && (
                    <div className="form-error-banner">
                      <span className="error-icon">!</span>
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  <div className="form-grid-layout">
                    {/* Left Column: Customer & Size selection */}
                    <div className="form-column">
                      <h3 className="form-column-title">Customer Information</h3>

                      <div className="form-fields-row">
                        <div className="field-group">
                          <label htmlFor="firstName">FIRST NAME *</label>
                          <input
                            id="firstName"
                            type="text"
                            name="firstName"
                            placeholder="Enter first name"
                            required
                            value={formData.firstName}
                            onChange={handleInputChange}
                          />
                        </div>

                        <div className="field-group">
                          <label htmlFor="lastName">LAST NAME *</label>
                          <input
                            id="lastName"
                            type="text"
                            name="lastName"
                            placeholder="Enter last name"
                            required
                            value={formData.lastName}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="form-fields-row">
                        <div className="field-group">
                          <label htmlFor="phone">PHONE NUMBER *</label>
                          <input
                            id="phone"
                            type="tel"
                            name="phone"
                            placeholder="e.g. 077 123 4567"
                            required
                            value={formData.phone}
                            onChange={handleInputChange}
                          />
                        </div>

                        <div className="field-group">
                          <label htmlFor="email">EMAIL ADDRESS *</label>
                          <input
                            id="email"
                            type="email"
                            name="email"
                            placeholder="e.g. name@example.com"
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      {/* Size Selector */}
                      <div className="size-selection-block">
                        <div className="size-label-row">
                          <label>SELECT T-SHIRT SIZE *</label>
                          <button
                            type="button"
                            className="size-chart-link-btn"
                            onClick={() => setShowSizeChart(true)}
                          >
                            Size Guide
                          </button>
                        </div>
                        <div className="size-pills-row">
                          {SIZES.map((s) => (
                            <button
                              key={s}
                              type="button"
                              className={`size-pill-btn ${size === s ? "active" : ""}`}
                              onClick={() => setSize(s)}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Quantity Stepper */}
                      <div className="quantity-selection-block">
                        <label>QUANTITY:</label>
                        <div className="stepper-controls">
                          <button
                            type="button"
                            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                            disabled={quantity <= 1}
                          >
                            −
                          </button>
                          <span className="stepper-val">{quantity}</span>
                          <button
                            type="button"
                            onClick={() => setQuantity((q) => Math.min(10, q + 1))}
                            disabled={quantity >= 10}
                          >
                            +
                          </button>
                        </div>
                        <span className="stepper-unit-note">
                          @ {formatLKR(TSHIRT_PRICE)} each
                        </span>
                      </div>
                    </div>

                    {/* Right Column: Payment & Bank Slip Upload */}
                    <div className="form-column">
                      <h3 className="form-column-title">Bank Payment & Slip</h3>

                      {/* Bank Details Card */}
                      <div className="bank-info-panel">
                        <div className="bank-row">
                          <span className="bank-label">BANK</span>
                          <span className="bank-val">People's Bank</span>
                        </div>
                        <div className="bank-row">
                          <span className="bank-label">BRANCH</span>
                          <span className="bank-val">Matara Uyanwatta (7135 152)</span>
                        </div>
                        <div className="bank-row">
                          <span className="bank-label">BENEFICIARY</span>
                          <span className="bank-val">S L Kurukulasuriya</span>
                        </div>
                        <div className="bank-row account-highlight-row">
                          <div>
                            <span className="bank-label">ACCOUNT NUMBER</span>
                            <span className="bank-val account-number-text">
                              032200360030474
                            </span>
                          </div>
                          <button
                            type="button"
                            className="copy-account-btn"
                            onClick={handleCopyAccount}
                          >
                            {copied ? "COPIED" : "COPY"}
                          </button>
                        </div>

                        <div className="total-due-strip">
                          <span>TOTAL TO TRANSFER:</span>
                          <strong className="total-lkr-val">
                            {formatLKR(TSHIRT_PRICE * quantity)}
                          </strong>
                        </div>
                      </div>

                      {/* Bank Payment Slip Section */}
                      <div className="slip-upload-block">
                        <label className="slip-label">
                          {FILE_UPLOADS_ENABLED ? "UPLOAD PAYMENT SLIP *" : "PAYMENT SLIP (GOOGLE DRIVE LINK) *"}
                        </label>
                        <p className="slip-hint">
                          {FILE_UPLOADS_ENABLED
                            ? "Upload receipt or transaction screenshot verifying transfer."
                            : "Upload your deposit slip / transaction receipt to Google Drive, set sharing to \"Anyone with the link\", and paste the link below."}
                        </p>

                        {!FILE_UPLOADS_ENABLED ? (
                          <div className="field-group" style={{ marginTop: "12px" }}>
                            <label htmlFor="slipLink">GOOGLE DRIVE LINK *</label>
                            <input
                              id="slipLink"
                              type="url"
                              name="slipLink"
                              placeholder="https://drive.google.com/..."
                              required
                              value={slipLink}
                              onChange={(e) => {
                                setSlipLink(e.target.value);
                                if (errorMessage) setErrorMessage("");
                              }}
                            />
                          </div>
                        ) : (
                          <div
                            className={`flat-upload-dropzone ${slipFile ? "file-ready" : ""}`}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <input
                              type="file"
                              ref={fileInputRef}
                              accept="image/*,.pdf"
                              onChange={handleFileChange}
                              style={{ display: "none" }}
                            />

                            {slipPreview ? (
                              <div className="slip-preview-box">
                                <img
                                  src={slipPreview}
                                  alt="Payment Slip"
                                  className="slip-preview-thumb"
                                />
                                <div className="file-meta">
                                  <span className="file-name-text">{slipFile.name}</span>
                                  <span className="change-file-text">Click to change slip</span>
                                </div>
                              </div>
                            ) : slipFile ? (
                              <div className="file-selected-box">
                                <svg
                                  className="flat-svg-icon"
                                  width="36"
                                  height="36"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.75"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                  <line x1="16" y1="13" x2="8" y2="13" />
                                  <line x1="16" y1="17" x2="8" y2="17" />
                                </svg>
                                <span className="file-name-text">{slipFile.name}</span>
                                <span className="change-file-text">Click to change slip</span>
                              </div>
                            ) : (
                              <div className="empty-upload-box">
                                <svg
                                  className="flat-svg-icon"
                                  width="38"
                                  height="38"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.75"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="17 8 12 3 7 8" />
                                  <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                <span className="upload-cta">
                                  <strong>Click to upload</strong> or drag payment slip here
                                </span>
                                <span className="upload-formats">
                                  Supports JPG, PNG, WEBP, or PDF
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Submit Order Button */}
                      <button
                        type="submit"
                        className="order-submit-button"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <span className="btn-spinner-wrap">
                            <svg
                              className="spinner-svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                              <path d="M12 2a10 10 0 0 1 10 10" />
                            </svg>
                            <span>{FILE_UPLOADS_ENABLED ? "UPLOADING & PLACING ORDER..." : "PLACING ORDER..."}</span>
                          </span>
                        ) : (
                          `CONFIRM ORDER // ${formatLKR(TSHIRT_PRICE * quantity)}`
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            ) : (
              /* Success Confirmation Screen (matching About page cards) */
              <div className="order-success-panel">
                <div className="success-icon-wrap">
                  <svg
                    width="44"
                    height="44"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#00ffcc"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>

                <h2 className="success-headline">ORDER PLACED SUCCESSFULLY</h2>
                <p className="success-message">
                  Thank you, <strong>{orderResult.fullName}</strong>. Your BTUI Signature
                  Tee Pack order has been logged into the system and is pending verification.
                </p>

                <div className="order-receipt-card">
                  <div className="receipt-ref-row">
                    <span className="ref-label">ORDER ID:</span>
                    <span className="ref-code">{orderResult.orderId}</span>
                  </div>

                  <div className="receipt-meta-grid">
                    <div>
                      <span className="item-label">ITEM</span>
                      <strong className="item-val">BTUI Signature Tee Pack</strong>
                    </div>
                    <div>
                      <span className="item-label">SIZE</span>
                      <strong className="item-val">{orderResult.size}</strong>
                    </div>
                    <div>
                      <span className="item-label">QUANTITY</span>
                      <strong className="item-val">{orderResult.quantity}</strong>
                    </div>
                    <div>
                      <span className="item-label">TOTAL PAID</span>
                      <strong className="item-val highlight-val">
                        {formatLKR(orderResult.total)}
                      </strong>
                    </div>
                    <div>
                      <span className="item-label">PHONE</span>
                      <span className="item-val">{orderResult.phone}</span>
                    </div>
                    <div>
                      <span className="item-label">EMAIL</span>
                      <span className="item-val">{orderResult.email}</span>
                    </div>
                    <div>
                      <span className="item-label">PAYMENT SLIP</span>
                      <span className="item-val">
                        {orderResult.slipUrl ? (
                          <a
                            href={orderResult.slipUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#ff4d4d", textDecoration: "underline" }}
                          >
                            {orderResult.slipFileName || "View Slip"}
                          </a>
                        ) : (
                          "Attached"
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="qr-receipt-section">
                    <p className="qr-title">PICKUP VERIFICATION QR CODE</p>
                    <div className="qr-img-box">
                      <img
                        src={orderResult.qrCode}
                        alt="Order QR Code"
                        className="qr-img"
                      />
                    </div>
                    <p className="qr-info">
                      Download or screenshot this QR code. Present it at the BTUI registration
                      desk on event day to collect your T-shirt.
                    </p>

                    <div className="receipt-buttons">
                      <button
                        type="button"
                        className="download-qr-btn"
                        onClick={handleDownloadQR}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        <span>DOWNLOAD QR CODE</span>
                      </button>
                      <button
                        type="button"
                        className="new-order-btn"
                        onClick={handleReset}
                      >
                        PLACE ANOTHER ORDER
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Stats Bar (mirroring AboutBTUI stats) */}
          <div className="merch-stats">
            <div className="merch-stat-item">
              <div className="merch-stat-number">LKR 2,500</div>
              <div className="merch-stat-label">OFFICIAL PRICE</div>
            </div>
            <div className="merch-stat-item">
              <div className="merch-stat-number">LIMITED</div>
              <div className="merch-stat-label">COMMITTEE EDITION</div>
            </div>
          </div>

          {/* Footer File Info (matching About.jsx footer) */}
          <div className="merchandise-file-info">
            <span>R</span>
            <span>C</span>
            <span>C</span>
            <span>S</span>
            <span>BTUI26_</span>
            <span className="file-number">TEE PACK: LKR 2,500</span>
          </div>
        </div>
      </div>

      {/* Size Chart Modal */}
      {showSizeChart && (
        <div className="merch-modal-overlay" onClick={() => setShowSizeChart(false)}>
          <div
            className="merch-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="merch-modal-header">
              <h3>T-SHIRT SIZE CHART & MEASUREMENTS</h3>
              <button
                type="button"
                className="merch-modal-close"
                onClick={() => setShowSizeChart(false)}
              >
                ✕
              </button>
            </div>
            <div className="merch-modal-body">
              <img
                src="/size-chart.jpeg"
                alt="Size Chart"
                className="size-chart-img"
              />
              <div className="size-tip-box">
                <p>
                  💡 <strong>Fitting Tip:</strong> When between two sizes, we recommend choosing
                  the larger size for a relaxed, comfortable fit.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}